import { useEffect, useMemo, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/Footer";
import { Icon } from "@/components/landing/Icon";
import { Reveal, staggerContainer, staggerItem } from "@/components/landing/Reveal";
import { AmbientOrbs } from "@/components/landing/Ambient";
import { ScrollProgress, WhatsAppFab } from "@/components/landing/Floating";
import { CATEGORY_META, IT_SUBCATEGORIES, TDS_SLUGS } from "@/lib/taxation";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";

const ACCENT = {
  emerald: { text: "text-emerald2-600", bg: "bg-emerald2-500/10", border: "border-emerald2-300/40" },
  gold: { text: "text-gold-700", bg: "bg-gold-500/10", border: "border-gold-300/50" },
};

const ServiceCard = ({ service, parentSlug, accent }) => (
  <motion.div variants={staggerItem}>
    <Link
      to={`/taxation/${parentSlug}/${service.slug}`}
      data-testid={`tax-service-card-${service.slug}`}
      className="group flex h-full flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_4px_16px_rgba(0,0,0,0.03)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_16px_36px_rgba(15,30,60,0.1)]"
    >
      <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${accent.bg} ${accent.text}`}>
        <Icon name={service.icon} className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1">
        <h3 className="text-sm font-bold leading-snug text-navy-900 group-hover:text-navy-700">{service.title}</h3>
        <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-slate-500">{service.subtitle}</p>
      </div>
      <span className="mt-auto flex items-center gap-1 text-xs font-semibold text-slate-500">
        View details <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </span>
    </Link>
  </motion.div>
);

export default function TaxationCategoryHub() {
  const { category } = useParams();
  const isGst = category === "gst";
  const isIncomeTax = category === "income-tax";
  const isTds = category === "tds";
  const apiCategory = isGst ? "GST Services" : isIncomeTax || isTds ? "Income Tax" : null;

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!apiCategory) return;
    setLoading(true);
    api
      .listServices(apiCategory)
      .then(({ services }) => setServices(isTds ? (services || []).filter((s) => TDS_SLUGS.includes(s.slug)) : (services || [])))
      .catch(() => setServices([]))
      .finally(() => setLoading(false));
  }, [apiCategory, isTds]);

  const itGroups = useMemo(
    () => IT_SUBCATEGORIES.map((sub) => ({ sub, items: services.filter((s) => s.sub_category === sub) })).filter((g) => g.items.length > 0),
    [services]
  );

  if (!isGst && !isIncomeTax && !isTds) return <Navigate to="/taxation" replace />;

  const meta = CATEGORY_META[apiCategory];
  const accent = ACCENT[meta.accent];
  const title = isGst ? "GST Services" : isTds ? "TDS Services" : "Income Tax Services";
  const parentSlug = isGst ? "gst" : isTds ? "tds" : "income-tax";

  const pageTitle = isGst
    ? "GST Registration & Return Filing Services | Biz360 Compliance"
    : isTds
    ? "TDS Filing & Certificate Advisory Services | Biz360 Compliance"
    : "Direct Tax, Income Tax & Advisory Services | Biz360 Compliance";

  const pageDesc = isGst
    ? "End-to-end GST services: GST registration, monthly GSTR-1 & 3B return filing, annual returns, LUT filing, and refund advisory for businesses in Maharashtra & India."
    : isTds
    ? "Expert TDS return filing (24Q, 26Q), Form 16/16A generation, lower deduction certificates, and Form 15CA/15CB support."
    : "Comprehensive direct tax services: individual & corporate ITR filing, tax audits, notice representation, and strategic tax planning.";

  return (
    <div className="relative min-h-screen bg-white font-sans text-navy-900 antialiased" data-testid="tax-category-page">
      <SEO
        title={pageTitle}
        description={pageDesc}
        canonical={`/taxation/${category}`}
        keywords={`${isGst ? "GST" : isTds ? "TDS" : "Income Tax"} filing India, tax consultant Pune, Maharashtra GST return, corporate tax advice`}
        breadcrumbs={[{ label: "Taxation", href: "/taxation" }, { label: title, href: `/taxation/${category}` }]}
      />
      <ScrollProgress />
      <Navbar />
      <main>
        <section className="relative overflow-hidden bg-[rgb(238,242,250)] pt-32 pb-14 md:pt-40 md:pb-16">
          <AmbientOrbs />
          <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
            <Reveal>
              <Link to="/taxation" className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy-700 transition-colors hover:text-gold-600">
                <ArrowLeft className="h-4 w-4" /> Back to Taxation
              </Link>
            </Reveal>
            <div className="mt-6 flex items-start gap-4">
              <span className={`grid h-12 w-12 shrink-0 place-items-center rounded-2xl ${accent.bg} ${accent.text}`}>
                <Icon name={isGst ? "Receipt" : isTds ? "Percent" : "Landmark"} className="h-6 w-6" />
              </span>
              <div>
                <Reveal delay={0.05}>
                  <h1 className="text-3xl font-bold tracking-tight text-navy-900 md:text-5xl">{title}</h1>
                </Reveal>
                <Reveal delay={0.1}>
                  <p className="mt-3 max-w-2xl text-base text-slate-600 md:text-lg">{meta.tagline}</p>
                </Reveal>
              </div>
            </div>
          </div>
        </section>

        <section className="relative bg-white py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            {loading ? (
              <p className="py-16 text-center text-slate-500">Loading services...</p>
            ) : isGst || isTds ? (
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                whileInView="show"
                viewport={{ once: false, margin: "-60px" }}
                className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
              >
                {services.map((s) => (
                  <ServiceCard key={s.slug} service={s} parentSlug={parentSlug} accent={accent} />
                ))}
              </motion.div>
            ) : (
              itGroups.map((group, gi) => (
                <div key={group.sub} className={gi > 0 ? "mt-14" : ""}>
                  <Reveal>
                    <h2 className="flex items-center gap-2.5 text-xl font-bold text-navy-900">
                      <span className={`h-2 w-2 rounded-full ${accent.bg.replace("/10", "")}`} />
                      {group.sub}
                    </h2>
                  </Reveal>
                  <motion.div
                    variants={staggerContainer}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: false, margin: "-60px" }}
                    className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
                  >
                    {group.items.map((s) => (
                      <ServiceCard key={s.slug} service={s} parentSlug={parentSlug} accent={accent} />
                    ))}
                  </motion.div>
                </div>
              ))
            )}
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFab />
    </div>
  );
}
